import { createTrackingConfig } from '../../components/generic/labels';
import { tableConfig, uiConfig } from '../../config/appConfig';
import CustomNonConformityReportSplitView from './CustomNonConformityReportSplitView';

export const nonConformityReportConfig = {
  entityName: 'NonConformityReport',
  
  // Entity information for file attachments
  entityTypeId: 5, // Non-Conformity Report entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/non-conformity-report',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
      // File attachment endpoints
      fileAttachments: (id) => `/${id}/file-attachments`,
      fileAttachmentCount: (id) => `/${id}/file-attachment-count`,
      withFileAttachments: (id) => `/${id}/with-file-attachments`
    },
    lookupEndpoints: {
      departments: '/department',
      users: '/non-conformity-report/relations/users',
      nonConformityTypes: '/non-conformity-type',
      nonConformityReportStatuses: '/non-conformity-report-status',
      priorities: '/priority',
      capas: '/capa'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/non-conformity-report',
      withFileAttachments: '/non-conformity-report/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default',  // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    // Default visible columns - only show the most important ones initially
    defaultVisibleColumns: [
      'code', 'title', 'nonConformityType', 'department', 'priority', 
      'severity', 'detectedBy', 'status', 'fileAttachmentCount'
    ],
  },

  // Custom Split View Panel configuration for Non-Conformity Report module
  customSplitViewPanel: {
    enabled: true,
    component: CustomNonConformityReportSplitView  // Use the custom Non-Conformity Report split view component
  },
  
  // Service configuration
  service: {
    name: 'nonConformityReportService',
    methods: {
      getAll: 'getAllNonConformityReports',
      getById: 'getNonConformityReportById', 
      create: 'createNonConformityReport',
      update: 'updateNonConformityReport',
      delete: 'deleteNonConformityReport',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Non-Conformity Report Management',
  description: 'Non-conformity reporting and resolution system for quality management',
  icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  
  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 5, // Non-Conformity Report master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
    enableSearch: true,
    enableFilters: true,
    enableExport: true,
    enableImport: true,
    enableAuditLog: true,
    enableComments: true,
    enableAttachments: true,
    enableWorkflow: true,
    enableNotifications: true
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in non-conformity report form
    showInDetails: true, // Show review and approval integration in non-conformity report details tab
    requireApprovalForClose: false, // Require review and approval before closing non-conformity report
    allowMultipleApprovals: true, // Allow multiple review and approval requests per non-conformity report
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in non-conformity report view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in non-conformity report timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },

  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'nonConformityType', 'department', 'priority', 'detectedBy', 
    'assignedTo', 'linkedCAPA', 'status', 'createdBy', 'updatedBy', 
    'closedBy', 'nonConformityReportTimeLineEntries', 'fileAttachments', 'fileAttachmentCount'
  ],

  // Field mappings for display values
  fieldMappings: {
    // Map frontend display fields to backend field paths
    department: 'department.name',
    nonConformityType: 'nonConformityType.name',
    priority: 'priority.name',
    status: 'status.name',
    detectedBy: 'detectedBy.userDisplayName',
    assignedTo: 'assignedTo.userDisplayName',
    linkedCAPA: 'linkedCAPA.title',
    closedBy: 'closedBy.userDisplayName'
  },

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'code',
      label: 'NCR Code',
      type: 'text',
      required: true,
      placeholder: 'Enter NCR code',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'NCR code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter NCR title',
      table: { header: 'Title', width: '200px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Title is required and must not exceed 200 characters'
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Enter NCR description',
      table: { header: 'Description', width: '200px', sortable: true },
      rows: 4,
      filterable: true,
      validation: {
        maxLength: 1000,
        message: 'Description must not exceed 1000 characters'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
    {
      name: 'severity',
      label: 'Severity (1-10)',
      type: 'number',
      required: false,
      placeholder: 'Enter severity level (1-10)',
      min: 1,
      max: 10,
      step: 1,
      table: { header: 'Severity', width: '100px', sortable: true },
      filterable: true,
      filterType: 'number',
      validation: {
        integer: true,
        min: 1,
        max: 10,
        message: 'Severity must be between 1 and 10'
      }
    },
    {
      name: 'rootCause',
      label: 'Root Cause',
      type: 'textarea',
      required: false,
      placeholder: 'Describe the root cause analysis',
      rows: 3,
      table: { header: 'Root Cause', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Root cause must not exceed 1000 characters'
      }
    },
    {
      name: 'resolutionDecision',
      label: 'Resolution Decision',
      type: 'textarea',
      required: false,
      placeholder: 'Describe the resolution decision',
      rows: 3,
      table: { header: 'Resolution Decision', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Resolution decision must not exceed 1000 characters'
      }
    },
    {
      name: 'nonConformityTypeId',
      label: 'NCR Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'nonConformityTypes',
      table: { 
        header: 'Type', 
        width: '130px', 
        sortable: true,
        displayField: 'nonConformityType',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid NCR type'
      }
    },
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      required: false,
      lookupEndpoint: 'departments',
      table: { 
        header: 'Department', 
        width: '150px', 
        sortable: true,
        displayField: 'department',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid department'
      }
    },
    {
      name: 'priorityId',
      label: 'Priority',
      type: 'select',
      required: false,
      lookupEndpoint: 'priorities',
      table: { 
        header: 'Priority', 
        width: '100px', 
        sortable: true,
        displayField: 'priority',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid priority'
      }
    },
    {
      name: 'customerImpact',
      label: 'Customer Impact',
      type: 'switch',
      required: false,
      defaultValue: false,
      validation: {
        message: 'Please select customer impact status'
      },
      table: { header: 'Customer Impact', width: '120px', sortable: true, displayField: 'customerImpact' },
      filterable: true,
      filterType: 'boolean',
      trueLabel: 'Yes',
      falseLabel: 'No'
    },
    {
      name: 'regulatoryImpact',
      label: 'Regulatory Impact',
      type: 'switch',
      required: false,
      defaultValue: false,
      validation: {
        message: 'Please select regulatory impact status'
      },
      table: { header: 'Regulatory Impact', width: '130px', sortable: true, displayField: 'regulatoryImpact' },
      filterable: true,
      filterType: 'boolean',
      trueLabel: 'Yes',
      falseLabel: 'No'
    },
    {
      name: 'financialImpact',
      label: 'Financial Impact',
      type: 'number',
      required: false,
      placeholder: 'Enter financial impact amount',
      validation: {
        min: 0,
        message: 'Financial impact must be a positive number'
      },
      table: { header: 'Financial Impact', width: '130px', sortable: true, displayField: 'financialImpact' },
      filterable: true,
      filterType: 'number',
      step: '0.01'
    },
    {
      name: 'operationalImpact',
      label: 'Operational Impact',
      type: 'textarea',
      required: false,
      placeholder: 'Describe operational impact',
      rows: 3,
      table: { header: 'Operational Impact', width: '260px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Operational impact must not exceed 1000 characters'
      }
    },
    {
      name: 'safetyImpact',
      label: 'Safety Impact',
      type: 'switch',
      required: false,
      defaultValue: false,
      validation: {
        message: 'Please select safety impact status'
      },
      table: { header: 'Safety Impact', width: '120px', sortable: true, displayField: 'safetyImpact' },
      filterable: true,
      filterType: 'boolean',
      trueLabel: 'Yes',
      falseLabel: 'No'
    },
    {
      name: 'qualityImpact',
      label: 'Quality Impact',
      type: 'textarea',
      required: false,
      placeholder: 'Describe quality impact',
      rows: 3,
      table: { header: 'Quality Impact', width: '260px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Quality impact must not exceed 1000 characters'
      }
    },
    {
      name: 'detectedById',
      label: 'Detected By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      table: { 
        header: 'Detected By', 
        width: '150px', 
        sortable: true,
        displayField: 'detectedBy',
        cellRenderer: 'user'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'detectionSource',
      label: 'Detection Source',
      type: 'text',
      required: false,
      placeholder: 'Enter detection source',
      table: { header: 'Detection Source', width: '150px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 200,
        message: 'Detection source must not exceed 200 characters'
      }
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      placeholder: 'Select due date',
      table: { header: 'Due Date', width: '150px', sortable: true, displayField: 'dueDate' },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid due date'
      }
    },
    {
      name: 'immediateActions',
      label: 'Immediate Actions',
      type: 'textarea',
      required: false,
      placeholder: 'Describe immediate actions taken',
      rows: 3,
      table: { header: 'Immediate Actions', width: '260px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Immediate actions must not exceed 1000 characters'
      }
    },
    {
      name: 'correctiveActions',
      label: 'Corrective Actions',
      type: 'textarea',
      required: false,
      placeholder: 'Describe corrective actions',
      rows: 3,
      table: { header: 'Corrective Actions', width: '260px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Corrective actions must not exceed 1000 characters'
      }
    },
    {
      name: 'preventiveActions',
      label: 'Preventive Actions',
      type: 'textarea',
      required: false,
      placeholder: 'Describe preventive actions',
      rows: 3,
      table: { header: 'Preventive Actions', width: '260px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Preventive actions must not exceed 1000 characters'
      }
    },
    {
      name: 'linkedCAPAId',
      label: 'Linked CAPA',
      type: 'select',
      required: false,
      lookupEndpoint: 'capas',
      table: { 
        header: 'Linked CAPA', 
        width: '150px', 
        sortable: true,
        displayField: 'linkedCAPA',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid CAPA'
      }
    },
    {
      name: 'closedAt',
      label: 'Closed Date',
      type: 'date',
      required: false,
      placeholder: 'Select closed date',
      table: { header: 'Closed Date', width: '150px', sortable: true, displayField: 'closedAt' },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid closed date'
      }
    },
    {
      name: 'closedById',
      label: 'Closed By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      table: { 
        header: 'Closed By', 
        width: '150px', 
        sortable: true,
        displayField: 'closedBy',
        cellRenderer: 'user'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
      },

    {
      name: 'assignedToId',
      label: 'Assigned To',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      table: {
        header: 'Assigned To',
        width: '150px',
        sortable: true,
        displayField: 'assignedTo',
        cellRenderer: 'user'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
      },

    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'nonConformityReportStatuses',
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      }
    },
    {
      name: 'detectedAt',
      label: 'Detected Date',
      type: 'date',
      required: false,
      table: { header: 'Detected Date', width: '150px', sortable: true, displayField: 'detectedAt' },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid detection date'
      }
    },
    {
      name: 'targetCompletionDate',
      label: 'Target Completion Date',
      type: 'date',
      required: false,
      table: { header: 'Target Completion', width: '170px', sortable: true, displayField: 'targetCompletionDate' },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid target completion date'
      }
    },
    {
      name: 'actualCompletionDate',
      label: 'Actual Completion Date',
      type: 'date',
      required: false,
      table: { header: 'Actual Completion', width: '170px', sortable: true, displayField: 'actualCompletionDate' },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid actual completion date'
      }
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
      {
        title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description', 'severity'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8',
        'description': 'md:col-span-12',
        'severity': 'md:col-span-4'
      }
      },
      {
        title: 'Analysis',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
        fields: ['rootCause', 'resolutionDecision']
      },
      {
        title: 'Classification',
      icon: '🏷️',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
        fields: ['nonConformityTypeId', 'departmentId', 'priorityId']
      },
      {
        title: 'Impact Assessment',
      icon: '⚠️',
      layout: 'grid',
      gridCols: 'md:grid-cols-4',
      fields: ['customerImpact', 'regulatoryImpact', 'safetyImpact',  ],
    },
    {
      title: 'Impact Details',
      icon: '📊',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['operationalImpact', 'qualityImpact', 'financialImpact',],
      fieldSpans: {
        'financialImpact': 'md:col-span-1'
      }
    },
    {
      title: 'Detection & Assignment',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['detectedAt', 'detectedById', 'detectionSource', 'assignedToId']
    },
    {
      title: 'Timeline & Dates',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['dueDate', 'targetCompletionDate', 'actualCompletionDate']
    },
    {
      title: 'Actions & Response',
      icon: '🎯',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['immediateActions', 'correctiveActions', 'preventiveActions']
    },
    {
      title: 'Status & Integration',
      icon: '🔄',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['statusId', 'linkedCAPAId'],
      fieldSpans: {
        'closedAt': 'md:col-span-1'
      }
    },
    {
      title: 'Closure',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['closedAt', 'closedById']
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Filter Groups Configuration - Groups filters into tabs for better organization
  // Based on form sections for consistency
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'code', 'title', 'statusId', 'nonConformityTypeId', 'departmentId', 
        'priorityId', 'severity', 'assignedToId'
      ]
    },
    {
      name: 'dates',
      label: 'Dates & Timeline',
      icon: '📅',
      fields: [
        'detectedAt', 'dueDate', 'targetCompletionDate', 'actualCompletionDate', 'closedAt'
      ]
    },
    {
      name: 'impact',
      label: 'Impact Assessment',
      icon: '⚠️',
      fields: [
        'customerImpact', 'regulatoryImpact', 'safetyImpact', 'financialImpact'
      ]
    },
    {
      name: 'detection',
      label: 'Detection & Assignment',
      icon: '🔍',
      fields: [
        'detectedById', 'detectionSource', 'linkedCAPAId', 'closedById'
      ]
    }
  ],

  // Table Configuration
  tableConfig: {
    defaultSort: { field: 'code', direction: 'desc' },
    defaultPageSize: 25,
    stickyHeader: true,
    rowSelection: true,
    exportOptions: ['csv', 'excel', 'pdf']
  },

  // Custom handlers for NCR-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in nonConformityReportPage
    onDelete: null, // Will be defined in nonConformityReportPage
    onLoad: null, // Backend now provides fileAttachmentCount directly
    onFormOpen: null, // Will be defined in nonConformityReportPage
    
    // File upload handlers
    onFileUpload: null, // Will handle file upload after NCR save
    onFileRemove: null // Will handle file removal
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fileAttachments from the main NCR data (handled separately)
    delete transformed.fileAttachments;
    
    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = [
      'nonConformityTypeId', 'departmentId', 'priorityId', 'detectedById',
      'assignedToId', 'statusId', 'linkedCAPAId', 'closedById'
    ];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert integer fields
    if (transformed.severity !== undefined) {
      if (transformed.severity === '' || transformed.severity === null) {
        delete transformed.severity;
      } else {
        const numValue = parseInt(transformed.severity, 10);
        if (!isNaN(numValue)) {
          transformed.severity = numValue;
        } else {
          delete transformed.severity;
        }
      }
    }

    // Convert decimal fields
    if (transformed.financialImpact !== undefined) {
      if (transformed.financialImpact === '' || transformed.financialImpact === null) {
        delete transformed.financialImpact;
      } else {
        const numValue = parseFloat(transformed.financialImpact);
        if (!isNaN(numValue)) {
          transformed.financialImpact = numValue;
        } else {
          delete transformed.financialImpact;
        }
      }
    }

    // Convert date fields to proper Date objects
    const dateFields = [
      'detectedAt', 'dueDate', 'targetCompletionDate', 
      'actualCompletionDate', 'closedAt'
    ];
    
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          // Convert date string to Date object
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Validation configuration
  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true
  },

  // Messages
  messages: {
    create: {
      success: 'Non-conformity report created successfully',
      error: 'Failed to create non-conformity report'
    },
    update: {
      success: 'Non-conformity report updated successfully', 
      error: 'Failed to update non-conformity report'
    },
    delete: {
      success: 'Non-conformity report deleted successfully',
      error: 'Failed to delete non-conformity report',
      confirm: 'Are you sure you want to delete this non-conformity report?'
    }
  },

  // Table columns generated from field configuration
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
    
    return columns;
  },

  exportFileName: 'qms_non_conformity_reports',

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('NonConformityReport'),

  // Labels and Text Configuration
  labels: {
    singular: 'Non-Conformity Report',
    plural: 'Non-Conformity Reports',
    pageTitle: 'Non-Conformity Report Management',
    pageSubtitle: 'Quality issue reporting and resolution for continuous improvement',
    searchPlaceholder: 'Search NCRs by code or title...'
  },

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  requiresApprovalForClose: function() {
    return this.isApprovalEnabled() && this.approval.requireApprovalForClose;
  },

  // Help text for field descriptions
  helpText: {
    code: { short: 'Unique NCR identifier used for tracking and reference.' },
    title: { short: 'Brief, descriptive title of the non-conformity issue.' },
    description: { short: 'Detailed description of the non-conformity found.' },
    fileAttachments: { short: 'Supporting documents, evidence, or related files for this NCR.' },
    severity: { short: 'Severity rating from 1 (low) to 10 (critical) based on impact assessment.' },
    rootCause: { short: 'Analysis of the underlying cause that led to this non-conformity.' },
    resolutionDecision: { short: 'Decision on how to resolve this non-conformity issue.' },
    nonConformityTypeId: { short: 'Category or type of non-conformity (process, product, system, etc.).' },
    departmentId: { short: 'Department where the non-conformity was detected or is being managed.' },
    priorityId: { short: 'Priority level based on risk and urgency of resolution.' },
    customerImpact: { short: 'Whether this non-conformity affects customers or end users.' },
    regulatoryImpact: { short: 'Whether this non-conformity has regulatory compliance implications.' },
    financialImpact: { short: 'Financial cost or impact of this non-conformity.' },
    operationalImpact: { short: 'Description of how this non-conformity affects operations.' },
    safetyImpact: { short: 'Whether this non-conformity poses safety risks.' },
    qualityImpact: { short: 'Description of how this non-conformity affects product/service quality.' },
    detectedById: { short: 'Person who first detected or reported this non-conformity.' },
    detectionSource: { short: 'Source or method through which the non-conformity was detected.' },
    dueDate: { short: 'Deadline for resolving this non-conformity.' },
    immediateActions: { short: 'Immediate actions taken to contain or address the non-conformity.' },
    correctiveActions: { short: 'Actions taken to correct the specific non-conformity found.' },
    preventiveActions: { short: 'Actions taken to prevent similar non-conformities in the future.' },
    linkedCAPAId: { short: 'Associated CAPA (Corrective and Preventive Action) if applicable.' },
    closedAt: { short: 'Date when the non-conformity was officially closed.' },
    closedById: { short: 'Person who closed this non-conformity report.' },
    assignedToId: { short: 'Person assigned to investigate and resolve this non-conformity.' },
    statusId: { short: 'Current status of the non-conformity in the resolution workflow.' },
    detectedAt: { short: 'Date when the non-conformity was first detected.' },
    targetCompletionDate: { short: 'Planned completion date for resolving this non-conformity.' },
    actualCompletionDate: { short: 'Actual date when the non-conformity was resolved.' }
  }
};

export default nonConformityReportConfig;
